import { Injectable, inject } from '@angular/core'
import { map } from 'rxjs/operators'

import { CookieService } from 'ngx-cookie-service'
import { User, UsuarioDados, UsuarioTipo } from '@/app/store/authentication/auth.model'
import { HttpClientService } from '@/app/services/http/http-client/http-client.service'

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  user: User | null = null

  public readonly authSessionKey = '_RIZZ_AUTH_SESSION_KEY_'
  public readonly userDataKey = '_RIZZ_AUTH_USER_DATA_KEY_'
  private cookieService = inject(CookieService)
  private httpClientService = inject(HttpClientService)

  login(email: string, password: string) {
    let senha = password;
    let usuario = email;
    return this.httpClientService
      ._postRequest<User>(`/auth-usuario/login`, { usuario, senha })
      .pipe(
        map((user) => {
          // login successful if there's a jwt token in the response
          if (user && user.token) {
            this.user = user
            // store user details and jwt in session
            this.saveSession(user.token)
          }
          if (user && user.usuario_dados) {
            this.saveUserData(user.usuario_dados)
          }
          return user
        })
      )
  }

  register(payload: {
    nome: string
    sobrenome: string
    email: string
    telefone: string
    data_nascimento: string
    senha: string
    senha_confirmar: string
    destino: 'email' | 'telefone'
  }) {
    return this.httpClientService._postRequest<{
      usuario: boolean
      id_usuario?: number
      destino?: string
    }>(`/auth-usuario/registrar`, payload)
  }

  /**
   * Creates a support-team account (tipo='suporte'). The backend does not check
   * the session/JWT for this endpoint yet, so authorization must always be sent
   * explicitly in the body: either the bootstrap invite code (only works while
   * no admin exists yet) or an existing admin's own usuario/senha. Same
   * confirmation-code flow as `register`.
   */
  registerSupport(payload: {
    nome: string
    sobrenome: string
    email: string
    telefone: string
    data_nascimento: string
    senha: string
    senha_confirmar: string
    destino: 'email' | 'telefone'
    codigoConvite?: string
    criador?: { usuario: string; senha: string }
  }) {
    return this.httpClientService._postRequest<{
      usuario: boolean
      id_usuario?: number
      destino?: string
    }>(`/auth-usuario/registrar-suporte`, payload)
  }

  verifyCode(payload: { id_usuario: number; codigo_confirmacao: string }) {
    return this.httpClientService._postRequest<{
      usuario: boolean
      code?: boolean
      token?: string
      usuario_id?: number
      usuario_dados?: UsuarioDados
    }>(`/auth-usuario/verificar-codigo-confirmacao`, payload)
  }

  resendCode(payload: { id_usuario: number; destino: 'email' | 'telefone' }) {
    return this.httpClientService._postRequest<{
      usuario: boolean
      destino?: string
    }>(`/auth-usuario/reenviar-codigo-confirmacao`, payload)
  }

  lookupUser(identifier: string) {
    return this.httpClientService._getRequest<{
      usuario: boolean
      id_usuario?: number
    }>(`/auth-usuario/verificar-usuario/${encodeURIComponent(identifier)}`)
  }

  logout(): void {
    // remove user from cookie to log user out
    this.removeSession()
    this.removeUserData()
    this.user = null
  }

  get session(): string {
    return this.cookieService.get(this.authSessionKey)
  }

  saveSession(token: string): void {
    this.cookieService.set(this.authSessionKey, token, undefined, '/')
  }

  removeSession(): void {
    this.cookieService.delete(this.authSessionKey, '/')
  }

  saveUserData(usuario_dados: UsuarioDados): void {
    this.cookieService.set(this.userDataKey, JSON.stringify(usuario_dados), undefined, '/')
  }

  removeUserData(): void {
    this.cookieService.delete(this.userDataKey, '/')
  }

  /** The logged-in user's profile (nome, sobrenome, tipo, ...), persisted at login. */
  get currentUserData(): UsuarioDados | null {
    const raw = this.cookieService.get(this.userDataKey)
    if (!raw) return null
    try {
      return JSON.parse(raw) as UsuarioDados
    } catch {
      return null
    }
  }

  /** 'cliente' | 'suporte' | 'admin' — use to branch UI without gating access. */
  get currentUserType(): UsuarioTipo | null {
    return this.currentUserData?.tipo ?? null
  }

  /** Admins aren't attached to a company — they get the support console view. */
  get isAdmin(): boolean {
    return this.currentUserType === 'admin'
  }

  get isSupport(): boolean {
    return this.currentUserType === 'suporte'
  }

  /** Merchants — the default for anyone who signs up normally. */
  get isClient(): boolean {
    return this.currentUserType === 'cliente' || this.currentUserType === null
  }
}
